import { AvailabilityService, RTKUtils } from '@sevenrooms/api'
import type { ISODate } from '@sevenrooms/domain'
import { DateOnly } from '@sevenrooms/timepiece'
import { baseReservationWidgetApi } from './BaseReservationWidgetApi'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'

const availabilityApiTags = ['Availability']

const DEFAULT_AVAILABILITY_CACHE_DAYS = 90
const NUM_DAYS_PER_REQUEST = 7

function getBeginOfTheWeek(date: string) {
  const parsedDate = new Date(date) || new Date()
  const today = parsedDate.getDate()
  const currentDay = parsedDate.getDay()
  const newDate = parsedDate.setDate(today - (currentDay || 7))
  return DateOnly.fromDate(new Date(newDate)).toIso()
}

function filterPassedAvailabilityDates(availability: Record<ISODate, Record<number, number>>, venueToday: string) {
  const today = new Date(venueToday)
  return Object.fromEntries(Object.entries(availability).filter(([date]) => new Date(date).getTime() >= today.getTime()))
}

export const availabilityApi = baseReservationWidgetApi.enhanceEndpoints({ addTagTypes: availabilityApiTags }).injectEndpoints({
  endpoints: builder => ({
    getSingleDayAvailability: builder.query({
      providesTags: availabilityApiTags,
      queryFn: RTKUtils.rtkQuery(AvailabilityService.getSingleDayAvailability),
    }),
    getRangeAvailability: builder.query({
      providesTags: availabilityApiTags,
      queryFn: RTKUtils.rtkQuery(AvailabilityService.getRange),
    }),
    getExperienceValidDates: builder.query({
      providesTags: ['ExperienceValidDates'],
      async queryFn(_arg, _api, _extraOptions) {
        try {
          const availabilityCacheDays = _arg.availabilityCacheDays || DEFAULT_AVAILABILITY_CACHE_DAYS
          const numberOfRequests = Math.ceil((availabilityCacheDays || DEFAULT_AVAILABILITY_CACHE_DAYS) / NUM_DAYS_PER_REQUEST)
          let combinedResult: Record<ISODate, Record<string, number>> = {}
          // this is how cache on the BE works - returns days starting from the beginning of the week
          let dateToFetch = getBeginOfTheWeek(_arg.venueToday)
          // this is necessary for initialization cache for getExperienceValidDates query
          _api.dispatch(
            availabilityApi.util.updateQueryData('getExperienceValidDates', _arg, data => {
              if (!data) {
                return {}
              }
              return { ...data }
            })
          )
          for (let i = 0; i < numberOfRequests; i += 1) {
            const res = await AvailabilityService.getExperienceValidDates({ ..._arg, startDate: dateToFetch })
            const availability = i === 0 ? filterPassedAvailabilityDates(res.availability, _arg.venueToday) : res.availability
            combinedResult = {
              ...combinedResult,
              ...availability,
            }
            _api.dispatch(availabilityApi.util.updateQueryData('getExperienceValidDates', _arg, data => ({ ...data, ...availability })))
            const date = new Date(Date.parse(dateToFetch))
            date.setDate(date.getDate() + NUM_DAYS_PER_REQUEST)
            dateToFetch = DateOnly.fromDate(date).toIso()
          }
          return { data: combinedResult, args: _arg }
        } catch (e) {
          return { error: e as FetchBaseQueryError }
        }
      },
    }),
  }),
})

export const { useGetSingleDayAvailabilityQuery, useGetRangeAvailabilityQuery, useGetExperienceValidDatesQuery } = availabilityApi
